import { useMemo, useState } from "react";
import { ICartItem, ICustomerSearchResult } from "../models/cashdeskModels";
import { UnitOfMeasure } from "../../../common/enums";
import styles from "./CashdeskCart.module.scss";

interface ICashdeskCartProps {
  cart: ICartItem[];
  updateQuantity: (productId: number, delta: number) => void;
  setQuantity: (productId: number, quantity: number) => void;
  selectedCustomer: ICustomerSearchResult | null;
  setSelectedCustomer: (customer: ICustomerSearchResult | null) => void;
  customerSearchQuery: string;
  setCustomerSearchQuery: (query: string) => void;
  customerSearchResults: ICustomerSearchResult[];
  discountPercent: number;
  setDiscountPercent: (percent: number) => void;
  subTotal: number;
  discountAmount: number;
  total: number;
  onPay: () => void;
}

export const CashdeskCart = ({
  cart,
  updateQuantity,
  setQuantity,
  selectedCustomer,
  setSelectedCustomer,
  customerSearchQuery,
  setCustomerSearchQuery,
  customerSearchResults,
  discountPercent,
  setDiscountPercent,
  subTotal,
  discountAmount,
  total,
  onPay,
}: ICashdeskCartProps) => {
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const customerSuggestions = useMemo(() => {
    if (selectedCustomer) return [];
    return customerSearchResults;
  }, [customerSearchResults, selectedCustomer]);

  const getInitials = (customer: ICustomerSearchResult) => {
    return `${customer.firstName[0] ?? ""}${customer.lastName[0] ?? ""}`.toUpperCase();
  };

  return (
    <>
      {/* Customer Section */}
      <div>
        <div className={styles.section_label}>Клиент (незадължително)</div>
        {!selectedCustomer ? (
          <div className={styles.customer_search}>
            <input
              className={styles.customer_input}
              type="text"
              placeholder="Търси клиент..."
              value={customerSearchQuery}
              onChange={(e) => {
                setCustomerSearchQuery(e.target.value);
                setShowCustomerDropdown(true);
              }}
              onFocus={() => setShowCustomerDropdown(true)}
              onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
            />
            {showCustomerDropdown && customerSuggestions.length > 0 && (
              <div className={styles.customer_dropdown}>
                {customerSuggestions.map((c) => (
                  <div
                    key={c.id}
                    className={styles.customer_dropdown_item}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSelectedCustomer(c);
                      setCustomerSearchQuery("");
                      setShowCustomerDropdown(false);
                    }}
                  >
                    <span className={styles.customer_avatar}>
                      {getInitials(c)}
                    </span>
                    <div>
                      <div className={styles.customer_dropdown_name}>
                        {c.firstName} {c.lastName}
                      </div>
                      {c.phoneNumber && (
                        <div className={styles.customer_dropdown_detail}>
                          {c.phoneNumber}
                        </div>
                      )}
                      {c.rfidBandId && (
                        <div className={styles.customer_dropdown_detail}>
                          🏷️ {c.rfidBandId}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.customer_badge}>
            <span className={styles.customer_avatar}>
              {getInitials(selectedCustomer)}
            </span>
            <span className={styles.customer_name}>
              {selectedCustomer.firstName} {selectedCustomer.lastName}
            </span>
            <button
              className={styles.customer_clear}
              onClick={() => setSelectedCustomer(null)}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div className={styles.divider} />

      {/* Cart Items */}
      <div className={styles.section_label}>Количка</div>
      <div className={styles.cart_list}>
        {cart.length === 0 ? (
          <div className={styles.cart_empty}>
            <div className={styles.cart_empty_icon}>🛒</div>
            <span>Добави продукти</span>
          </div>
        ) : (
          cart.map((item) => {
            const isGram = item.unitOfMeasure === UnitOfMeasure.Gram;
            const unitLabel = isGram ? "гр." : "бр.";
            const isEditing = editingProductId === item.productId;
            // A gift voucher is a unique numbered instrument, not a countable
            // article: its quantity is fixed at 1, so it gets a remove button
            // instead of a quantity stepper.
            const isGiftVoucher = !!item.giftVoucherId;

            return (
              <div key={item.productId} className={styles.cart_item}>
                <div className={styles.cart_item_emoji}>{item.emoji}</div>
                <div className={styles.cart_item_info}>
                  <div className={styles.cart_item_name}>{item.name}</div>
                  <div className={styles.cart_item_price}>
                    {item.price.toFixed(2)} €
                    {isGiftVoucher ? " · номинал" : ` / ${unitLabel}`}
                  </div>
                </div>
                {isGiftVoucher ? (
                  <div className={styles.qty_ctrl}>
                    <button
                      className={styles.qty_btn}
                      title="Премахни ваучера"
                      aria-label="Премахни ваучера"
                      onClick={() => setQuantity(item.productId, 0)}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                <div className={styles.qty_ctrl}>
                  <button
                    className={styles.qty_btn}
                    onClick={() => updateQuantity(item.productId, isGram ? -10 : -1)}
                  >
                    −
                  </button>
                  {isEditing ? (
                    <input
                      className={styles.qty_input}
                      type="number"
                      value={editingValue}
                      autoFocus
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={() => {
                        const val = Math.max(0, Number(editingValue) || 0);
                        if (val > 0) {
                          setQuantity(item.productId, val);
                        }
                        setEditingProductId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  ) : (
                    <span
                      className={styles.qty_num}
                      onClick={() => {
                        setEditingProductId(item.productId);
                        setEditingValue(String(item.quantity));
                      }}
                      title="Натисни за ръчно въвеждане"
                    >
                      {item.quantity}
                    </span>
                  )}
                  <button
                    className={styles.qty_btn}
                    onClick={() => updateQuantity(item.productId, isGram ? 10 : 1)}
                  >
                    +
                  </button>
                </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className={styles.divider} />

      {/* Discount */}
      <div className={styles.discount_row}>
        <span className={styles.discount_label}>Отстъпка (%)</span>
        <input
          className={styles.discount_input}
          type="number"
          min="0"
          max="100"
          value={discountPercent || ""}
          onChange={(e) => {
            const val = Math.min(100, Math.max(0, Number(e.target.value) || 0));
            setDiscountPercent(val);
          }}
          onWheel={(e) => e.currentTarget.blur()}
          placeholder="0"
        />
      </div>

      {/* Totals */}
      <div className={styles.totals}>
        <div className={styles.total_row}>
          <span className={styles.total_label}>Сума</span>
          <span className={styles.total_val}>{subTotal.toFixed(2)} €</span>
        </div>
        {discountPercent > 0 && subTotal > 0 && (
          <div className={styles.total_row}>
            <span className={styles.total_label}>Отстъпка</span>
            <span className={styles.total_val_discount}>
              -{discountAmount.toFixed(2)} €
            </span>
          </div>
        )}
        <div className={styles.total_row}>
          <span className={styles.section_label}>Общо</span>
          <span className={styles.total_final}>{total.toFixed(2)} €</span>
        </div>
      </div>

      {/* Pay Button */}
      <button
        className={styles.pay_btn}
        disabled={cart.length === 0}
        onClick={onPay}
      >
        💳 Плати
      </button>
    </>
  );
};
